import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, CalendarClock, IndianRupee, X } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      year: "numeric", month: "short", day: "2-digit",
    });
  } catch { return iso; }
};

// Default the new cycle end to +1 month / +1 year of the current end date
// (or of "today" if there is no current end date), so the super-admin
// usually just hits Confirm. They can override the date in the input.
const defaultNewEnd = (row) => {
  const cycle = (row.billing_cycle || "monthly").toLowerCase();
  const base = row.plan_end_date ? new Date(row.plan_end_date) : new Date();
  if (cycle === "yearly") base.setFullYear(base.getFullYear() + 1);
  else base.setMonth(base.getMonth() + 1);
  // Normalize to yyyy-mm-dd for an <input type="date"> field.
  const yyyy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, "0");
  const dd = String(base.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function SuperAdminRenewals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // Modal state — one row at a time. `mode` is "receive" or "extend".
  const [modal, setModal] = useState(null);
  const [modalDate, setModalDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/renewals");
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load renewals");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openReceive = (row) => {
    setModal({ row, mode: "receive" });
    setModalDate(defaultNewEnd(row));
  };
  const openExtend = (row) => {
    setModal({ row, mode: "extend" });
    // Default extend target = current plan_end_date + 1 month (gentle nudge).
    setModalDate(defaultNewEnd(row));
  };
  const closeModal = () => { setModal(null); setModalDate(""); };

  const confirmReceive = async () => {
    if (!modal) return;
    const { row } = modal;
    if (!modalDate) { toast.error("Please pick a new end date"); return; }
    setBusy(`receive-${row.tenant_id}`);
    try {
      const { data } = await api.post(
        `/superadmin/tenants/${row.tenant_id}/subscription/receive-renewal`,
        { new_end_date: modalDate },
      );
      const amt = data.amount ? `₹${Number(data.amount).toLocaleString("en-IN")}` : "renewal";
      toast.success(
        `Received ${amt} for ${row.name}. New cycle: ${fmtDate(data.plan_start_date)} → ${fmtDate(data.plan_end_date)}`,
      );
      closeModal();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Receive renewal failed");
    } finally { setBusy(null); }
  };

  const confirmExtend = async () => {
    if (!modal) return;
    const { row } = modal;
    if (!modalDate) { toast.error("Please pick a new end date"); return; }
    setBusy(`extend-${row.tenant_id}`);
    try {
      const { data } = await api.post(
        `/superadmin/tenants/${row.tenant_id}/subscription/extend`,
        { new_end_date: modalDate },
      );
      toast.success(`Extended ${row.name} to ${fmtDate(data.plan_end_date)}`);
      closeModal();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    } finally { setBusy(null); }
  };

  const extendByDays = async (row, days) => {
    setBusy(`extend-${row.tenant_id}-${days}`);
    try {
      const { data } = await api.post(
        `/superadmin/tenants/${row.tenant_id}/subscription/extend`,
        { days },
      );
      toast.success(`Extended ${row.name} to ${fmtDate(data.plan_end_date)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-7xl" data-testid="super-renewals-page">
      <div className="overline mb-2">Billing</div>
      <h1 className="font-display text-4xl mb-6 flex items-center gap-3">
        <CalendarClock /> Tenant Renewals
      </h1>

      {loading ? (
        <div className="text-sm text-[hsl(var(--kb-muted))] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading renewals…
        </div>
      ) : (
        <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
          <table className="w-full text-sm" data-testid="renewals-table">
            <thead className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] bg-[hsl(var(--kb-surface-alt))]">
              <tr>
                <th className="text-left p-3">Tenant</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-left p-3">Plan start</th>
                <th className="text-left p-3">Plan end</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Amount (₹)</th>
                <th className="text-left p-3">Last paid</th>
                <th className="text-right p-3">Receive renewal</th>
                <th className="text-right p-3">Extend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--kb-border))]">
              {rows.map((r) => {
                const d = r.days_remaining;
                const statusCls = {
                  overdue: "bg-red-100 text-red-800",
                  upcoming: "bg-amber-100 text-amber-800",
                  trialing: "bg-blue-100 text-blue-800",
                  active: "bg-emerald-100 text-emerald-800",
                }[r.status] || "bg-gray-100 text-gray-800";
                return (
                  <tr key={r.tenant_id} data-testid={`renewal-row-${r.slug}`}>
                    <td className="p-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[11px] text-[hsl(var(--kb-muted))]">/{r.slug}</div>
                      {r.is_suspended && (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-red-700 border border-red-700/30 px-1.5 py-0.5">
                          Suspended
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div>{r.plan_name || r.plan_slug || "—"}</div>
                      {r.billing_cycle && (
                        <div className="text-[10px] text-[hsl(var(--kb-muted))] uppercase tracking-wider">
                          {r.billing_cycle}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs" data-testid={`plan-start-${r.slug}`}>{fmtDate(r.plan_start_date)}</td>
                    <td className="p-3 font-mono text-xs" data-testid={`plan-end-${r.slug}`}>
                      {fmtDate(r.plan_end_date)}
                      <div className={`text-[10px] font-mono mt-0.5 ${d == null ? "" : d < 0 ? "text-red-700" : d <= 7 ? "text-amber-700" : "text-[hsl(var(--kb-muted))]"}`}>
                        {d == null ? "" : d < 0 ? `${Math.abs(d)}d overdue` : `${d}d left`}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${statusCls}`} data-testid={`status-${r.slug}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono">
                      ₹{(r.renewal_amount_inr || 0).toLocaleString("en-IN")}
                      {r.pending_invoice_id && (
                        <div className="text-[10px] text-amber-700 uppercase tracking-wider">pending</div>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {r.last_paid_at ? (
                        <>
                          <div className="font-mono">{fmtDate(r.last_paid_at)}</div>
                          {r.last_paid_amount ? (
                            <div className="text-[10px] text-[hsl(var(--kb-muted))]">
                              ₹{Number(r.last_paid_amount).toLocaleString("en-IN")}
                            </div>
                          ) : null}
                        </>
                      ) : <span className="text-[hsl(var(--kb-muted))]">never</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        className="kb-btn-primary !py-1.5 !px-3 !text-[11px] inline-flex items-center gap-1"
                        disabled={busy === `receive-${r.tenant_id}`}
                        onClick={() => openReceive(r)}
                        data-testid={`receive-renewal-${r.slug}`}
                        title={r.pending_invoice_id
                          ? "Mark the pending invoice as paid and roll the cycle forward (choose new end date)"
                          : "Generate the next invoice, mark it paid, and roll the cycle forward (choose new end date)"}
                      >
                        {busy === `receive-${r.tenant_id}`
                          ? <Loader2 size={11} className="animate-spin" />
                          : <IndianRupee size={11} />}
                        Receive payment
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1 items-center">
                        {[7, 30, 90].map((days) => (
                          <button
                            key={days}
                            className="kb-btn-outline !py-1 !px-2 !text-[10px]"
                            disabled={busy === `extend-${r.tenant_id}-${days}`}
                            onClick={() => extendByDays(r, days)}
                            data-testid={`extend-${r.slug}-${days}`}
                          >
                            +{days}d
                          </button>
                        ))}
                        <button
                          className="kb-btn-outline !py-1 !px-2 !text-[10px]"
                          onClick={() => openExtend(r)}
                          data-testid={`extend-custom-${r.slug}`}
                          title="Pick an exact end date"
                        >
                          Custom…
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-sm text-[hsl(var(--kb-muted))]">No tenants found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <RenewalDateModal
          row={modal.row}
          mode={modal.mode}
          value={modalDate}
          onChange={setModalDate}
          onClose={closeModal}
          onConfirm={modal.mode === "receive" ? confirmReceive : confirmExtend}
          busy={busy === (modal.mode === "receive"
            ? `receive-${modal.row.tenant_id}`
            : `extend-${modal.row.tenant_id}`)}
        />
      )}
    </div>
  );
}

function RenewalDateModal({ row, mode, value, onChange, onClose, onConfirm, busy }) {
  const cycle = (row.billing_cycle || "monthly").toLowerCase();
  const defaultLabel = cycle === "yearly" ? "+1 year" : "+1 month";
  // The minimum acceptable date is the day AFTER the current plan end
  // (or tomorrow when there's no end date) — extending backwards or to
  // "today" would either suspend the tenant immediately or be a no-op.
  const minDate = useMemo(() => {
    const base = row.plan_end_date ? new Date(row.plan_end_date) : new Date();
    base.setDate(base.getDate() + 1);
    return base.toISOString().slice(0, 10);
  }, [row.plan_end_date]);

  const isReceive = mode === "receive";
  const title = isReceive ? "Receive renewal payment" : "Extend subscription";
  const cta = isReceive ? "Confirm payment & roll forward" : "Update end date";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      data-testid="renewal-modal"
    >
      <div className="bg-white border border-[hsl(var(--kb-border))] w-full max-w-md p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline">{row.billing_cycle || "monthly"}</div>
            <h2 className="font-display text-2xl">{title}</h2>
            <div className="text-sm text-[hsl(var(--kb-muted))] mt-1">
              {row.name} <span className="font-mono">/{row.slug}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-1 hover:bg-[hsl(var(--kb-surface-alt))]"
            data-testid="renewal-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 bg-[hsl(var(--kb-surface-alt))] p-3 border border-[hsl(var(--kb-border))]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Current plan end</div>
              <div className="font-mono text-xs mt-0.5">{fmtDate(row.plan_end_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Amount</div>
              <div className="font-mono text-xs mt-0.5">
                ₹{(row.renewal_amount_inr || 0).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">
              New plan end date <span className="text-[hsl(var(--kb-primary))]">(default {defaultLabel})</span>
            </label>
            <input
              type="date"
              className="w-full mt-1 border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
              value={value}
              min={minDate}
              onChange={(e) => onChange(e.target.value)}
              data-testid="renewal-modal-date"
            />
            <div className="flex gap-1 mt-2">
              {[
                { label: "+1 month", days: 30 },
                { label: "+3 months", days: 90 },
                { label: "+6 months", days: 180 },
                { label: "+1 year", days: 365 },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  className="kb-btn-outline !py-1 !px-2 !text-[10px]"
                  onClick={() => onChange(todayPlus(opt.days))}
                  data-testid={`renewal-modal-quick-${opt.days}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {isReceive && (
            <p className="text-xs text-[hsl(var(--kb-muted))] leading-relaxed">
              {row.pending_invoice_id
                ? "The pending invoice will be marked PAID and the plan window will move to the date above."
                : "A new invoice will be generated, marked PAID, and the plan window will move to the date above."}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={busy}
            className="kb-btn-outline !py-1.5 !px-3 !text-[11px]"
            data-testid="renewal-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="kb-btn-primary !py-1.5 !px-3 !text-[11px] inline-flex items-center gap-1"
            data-testid="renewal-modal-confirm"
          >
            {busy && <Loader2 size={11} className="animate-spin" />}
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}
